import {
    ReactElement,
    useRef,
    ChangeEventHandler,
    FormEventHandler,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState
} from 'react';
import { Company } from '@/api';
import {
    useUpdateCompanyAction,
    useUploadCompanyImageAction
} from '../actions';
import {
    UIToast,
    UIAvatar,
    UIButton,
    UIFormInput,
    UIFormInputRef,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type GeneralCompanySettingsSectionProps = { company: Company };

export function GeneralCompanySettingsSection(
    props: GeneralCompanySettingsSectionProps
): ReactElement {
    const { company } = props;
    const { t } = useCompaniesTranslation();

    const formRef = useRef<GeneralCompanySettingsSectionFormRef>(null);
    const updateCompany = useUpdateCompanyAction({
        companyId: company.id,
        successMessage: t(
            'components.general_company_settings_section.toast_updated'
        )
    });
    const uploadCompanyImage = useUploadCompanyImageAction({
        companyId: company.id
    });

    function onSubmit(data: { companyName: string }) {
        const { companyName } = data;
        updateCompany.updateCompany({ companyName }).catch(() => {}); // Error handled in the action
    }

    function onUploadImage(file: File) {
        uploadCompanyImage
            .uploadImage(file)
            .then(() => {
                UIToast.success(
                    t(
                        'components.general_company_settings_section.toast_image_updated'
                    )
                );
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <GeneralCompanySettingsSectionForm
            canEdit={updateCompany.isAllowed}
            ref={formRef}
            initialCompanyName={company.name}
            initialCompanyImage={company.image}
            loading={updateCompany.loading}
            imageLoading={uploadCompanyImage.loading}
            onSubmit={onSubmit}
            onUploadImage={onUploadImage}
        />
    );
}

type GeneralCompanySettingsSectionFormProps = {
    canEdit: boolean;
    initialCompanyName: string;
    initialCompanyImage: string;
    imageLoading: boolean;
    loading: boolean;
    onSubmit: (data: { companyName: string }) => void;
    onUploadImage: (file: File) => void;
};

type GeneralCompanySettingsSectionFormRef = {
    resetForm: () => void;
};

const GeneralCompanySettingsSectionForm = forwardRef<
    GeneralCompanySettingsSectionFormRef,
    GeneralCompanySettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        initialCompanyName,
        initialCompanyImage,
        loading,
        onSubmit,
        onUploadImage
    } = props;
    const { t } = useCompaniesTranslation();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleFileInputClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                onUploadImage(file);
            }
        }
    };

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const companyNameRef = useRef<UIFormInputRef>(null);
    const [companyName, setCompanyName] = useState(initialCompanyName);
    const [companyNameError, setCompanyNameError] = useState('');
    const checkCompanyName = useCallback(
        (companyName: string): string => {
            const trimmed = companyName.trim();
            if (trimmed.length === 0) {
                return t(
                    'components.general_company_settings_section.name_error_empty'
                );
            } else if (trimmed.length > 100) {
                return t(
                    'components.general_company_settings_section.name_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setCompanyNameError(checkCompanyName(companyName));
        }
    }, [companyName, showRealTimeErrors, checkCompanyName]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkCompanyName(companyName)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ companyName });
        }
    };

    const haveValuesChanged = useMemo(() => {
        return companyName !== initialCompanyName;
    }, [companyName, initialCompanyName]);

    function resetForm() {
        setCompanyName(initialCompanyName);
        setCompanyNameError('');
        setShowRealTimeErrors(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader title={t('shared.labels.general')} />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    gap: 16,
                                    alignItems: 'center'
                                }}
                            >
                                <UIAvatar
                                    size={64}
                                    img={initialCompanyImage}
                                    alt=""
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4
                                    }}
                                >
                                    <UIText>
                                        {t(
                                            'components.general_company_settings_section.image_label'
                                        )}
                                    </UIText>
                                    <UIText type="default2">
                                        {t(
                                            'components.general_company_settings_section.image_description'
                                        )}
                                    </UIText>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                {canEdit && (
                                    <>
                                        <UIButton
                                            text={t('shared.labels.edit')}
                                            type="button"
                                            onClick={handleFileInputClick}
                                            loading={props.imageLoading}
                                            disabled={props.imageLoading}
                                        />
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            accept=".jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.general_company_settings_section.name_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.general_company_settings_section.name_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={companyNameRef}
                                    name="Company Name"
                                    value={companyName}
                                    placeholder={t(
                                        'components.general_company_settings_section.name_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={companyNameError}
                                    required
                                    maxLength={100}
                                    onChange={setCompanyName}
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                {haveValuesChanged && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});
